import React, { useEffect, useState } from 'react';
import axiosInstance from '../../utils/setupAxios';

function InfoRow({ label, value }) {
  return (
    <div className="row mb-2">
      <div className="col-5 text-muted small">{label}</div>
      <div className="col-7 small fw-medium" style={{ wordBreak: 'break-all' }}>
        {value === null || value === undefined || value === 'null' ? (
          <span className="text-muted fst-italic">—</span>
        ) : value === '' ? (
          <span className="text-muted fst-italic">empty</span>
        ) : (
          String(value)
        )}
      </div>
    </div>
  );
}

function ConfigItemCard({ item }) {
  return (
    <div className="card h-100">
      <div className="card-header d-flex align-items-center justify-content-between">
        <span className="fw-bold">{item.code}</span>
        <span className={`badge ${item.enable ? 'bg-success-lt text-success' : 'bg-danger-lt text-danger'}`}>
          {item.enable ? 'Enabled' : 'Disabled'}
        </span>
      </div>
      <div className="card-body">
        <InfoRow label="ID" value={item.id} />
        <InfoRow label="Service" value={item.service} />
        <InfoRow label="Main Service" value={item.main_service} />
        <InfoRow label="Backup Service" value={item.backup_service} />
        <InfoRow label="Backup Countries" value={item.backup_service_countries} />
        <InfoRow label="Disable Countries" value={item.disable_countries} />
        <InfoRow label="Free Usage / Day" value={item.free_usage_per_day} />
        <InfoRow label="API Key" value={item.api_key || ''} />
      </div>
    </div>
  );
}

function MonetizationCard({ monetization }) {
  const { event_threshold_in_milliseconds, iap_offer, ...rest } = monetization;
  return (
    <div className="card">
      <div className="card-header">
        <h4 className="card-title mb-0">Monetization</h4>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-md-6">
            <InfoRow
              label="Event Threshold"
              value={event_threshold_in_milliseconds != null ? `${event_threshold_in_milliseconds} ms` : null}
            />
            {Object.entries(rest).map(([k, v]) => (
              <InfoRow key={k} label={k} value={typeof v === 'object' ? JSON.stringify(v) : v} />
            ))}
          </div>
          {iap_offer && (
            <div className="col-md-6">
              <div className="text-muted small mb-2 fw-semibold">IAP Offer</div>
              {Object.entries(iap_offer).map(([k, v]) => (
                <InfoRow key={k} label={k} value={v} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CoinConfig() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const res = await axiosInstance.get('/coins/get-config');
        if (!cancelled) setData(res?.data ?? null);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, []);

  const items = data?.data?.items ?? [];
  const monetization = data?.data?.monetization ?? null;

  return (
    <div className="container-xl">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h2 className="page-title">Coin Config</h2>
            <div className="text-muted">GET <code>/coins/get-config</code></div>
          </div>
        </div>
      </div>

      {loading && <div className="text-center py-5 text-muted">Loading…</div>}

      {error && (
        <div className="alert alert-danger" role="alert">
          {String(error?.message || error)}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {items.length > 0 && (
            <div className="mb-4">
              <h4 className="mb-3">Services</h4>
              <div className="row g-3">
                {items.map((item) => (
                  <div key={item.id} className="col-md-6 col-lg-4">
                    <ConfigItemCard item={item} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {monetization && (
            <div className="mb-4">
              <MonetizationCard monetization={monetization} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

